import { newNeuroThreeTariffsListId, newNeuroThreeTariffsPopupTitle } from 'reactApp/appHelpers/featuresHelpers/features/aiTariff';
import { MultiSessionDeviceSubs } from 'reactApp/appHelpers/featuresHelpers/features/multiSessionSubscriptions';
import { openBuyDisableAds, openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import type { Product } from 'reactApp/types/Billing';
import { getPrice } from 'reactApp/ui/TariffSectionNew/helpers/getPrice';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { getDiscount } from 'reactApp/utils/getDiscount';
import { getFirstSomePeriodsAsWord, getPeriodName, isMonthPeriod, isYearPeriod } from 'reactApp/utils/Period';
import { computersNominative } from 'reactApp/utils/pluralHelpers';
import { formatPrice, formatWoNbspRuPrice } from 'reactApp/utils/priceHelpers';
import type { AnyAction, Dispatch } from 'redux';

export const getMultiSessionFreeChoiceDescription = (product: Product) => {
    const devices = MultiSessionDeviceSubs.getDeviceCount(product.id);
    const [priceAsText] = getPrice({ product, periodDivider: 'в', locale: 'ru' });
    return `${MultiSessionDeviceSubs.deviceInSubs} ${computersNominative(MultiSessionDeviceSubs.deviceInSubs)} бесплатно, ${
        devices - MultiSessionDeviceSubs.deviceInSubs
    } - за ${priceAsText}`
        .replace('\u00A0', ' ')
        .replace('₽', 'руб.');
};

export const getMultiSessionPresetInSubsDescription = (product: Product, renewalPrice?: number) => {
    const devices = MultiSessionDeviceSubs.getDeviceCount(product.id);
    const [_, discountPrice = product.price] = getPrice({ product, periodDivider: 'в', locale: 'ru' });
    let isDiscount = true;
    if (renewalPrice) {
        // мы смотрим уже продление
        isDiscount = product.discountPrice === renewalPrice;
    }
    const subPrice = MultiSessionDeviceSubs.getSubPrices(isMonthPeriod(product.discountPeriod || product.period), isDiscount);
    const restPrice = renewalPrice ? renewalPrice - subPrice : discountPrice - subPrice;
    return `+${product.space.space} ${product.space.units} c подпиской за ${formatWoNbspRuPrice(
        subPrice
    )} руб. и ${devices} ${computersNominative(devices)} за ${formatWoNbspRuPrice(restPrice)} в ${getPeriodName(
        product.discountPeriod || product.period
    )}`
        .replace('\u00A0', ' ')
        .replace('₽', 'руб.');
};

export const getMidasDescription = (product: Product): string => {
    if (!product) {
        return '';
    }

    const { hasDiscount, trialPeriod, discountPeriodCount, discountPeriod, isOnlyFeatures, hasTrial, isFamily, discountPrice, price } =
        product;

    const trialPeriodName = trialPeriod && getPeriodName(trialPeriod, false, false, true).replace('\u00A0', ' ');
    const discountPeriodName =
        discountPeriod &&
        getFirstSomePeriodsAsWord(discountPeriodCount || 1, isYearPeriod(discountPeriod) ? 'y' : 'm').replace('\u00A0', ' ');

    /* tempexp_20439-start */
    if (product?.id && MultiSessionDeviceSubs.enabled) {
        if (MultiSessionDeviceSubs.isFreeChoiceProduct(product?.id)) {
            return getMultiSessionFreeChoiceDescription(product);
        }
        if (MultiSessionDeviceSubs.isPresetInSubsProduct(product?.id)) {
            return getMultiSessionPresetInSubsDescription(product);
        }
    }
    /* tempexp_20439-end */

    if (product && isFamily && isOnlyFeatures && !hasTrial) {
        return `Семейные функции на ${getPeriodName(product.discountPeriod || product.period, false, true)}`;
    }

    const spaceText = `+${product.space.space} ${product.space.units}`;

    if (hasDiscount && trialPeriodName && discountPeriodName && !isOnlyFeatures) {
        return `${spaceText} на ${trialPeriodName} бесплатно, далее ${formatPrice(
            product.discountPrice,
            'ru',
            false,
            false
        )} руб. в ${discountPeriodName}`;
    }

    if (trialPeriodName && !isOnlyFeatures) {
        return `${spaceText} на ${trialPeriodName} бесплатно, далее ${formatPrice(
            product.price,
            'ru',
            false,
            false
        )} руб. в ${getPeriodName(product.period)}`;
    }

    if (trialPeriodName) {
        return 'Для проверки карты мы спишем и сразу же вернем 1 руб.';
    }

    if (discountPeriodName) {
        const { discount } = getDiscount({ discountPrice: discountPrice || 0, price });

        return `${spaceText} на ${discountPeriodName} со скидкой ${discount}%`;
    }

    return '';
};

export const openThreeTariffsPopup = ({
    dispatch,
    paySource,
    title = newNeuroThreeTariffsPopupTitle,
    buyTitle = '',
    tariffsId = newNeuroThreeTariffsListId,
}: {
    dispatch: Dispatch<AnyAction>;
    paySource: string;
    title?: string;
    buyTitle?: string;
    tariffsId?: string;
}) => {
    dispatch(
        openBuyDisableAds({
            title,
            tariffsNumber: tariffsId,
            activeTab: ETabsName.year,
            isFrame: false,
            onBuyClick: (product: Product) =>
                dispatch(
                    openTariffBuy({
                        productId: product.id,
                        title: buyTitle,
                        paySource,
                        onSuccess: () => closePopupHelper(popupNames.BUY_DISABLE_ADS),
                    })
                ),
            paySource,
        })
    );
};
